import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logoImage from "../images/zta_logo.webp";
import { gtagTrackBookDemo } from "../util/GoogleTagUtils";
import "./Navbar.css";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/strategies", label: "Strategies" },
  { path: "/portfolio_analyzer", label: "Portfolio Analyzer" },
  { path: "/about", label: "About" },
  { path: "/faq", label: "FAQ" },
];

const Navbar = React.memo(() => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const skipClickRef = useRef(false);
  const ignoreToggleUntilRef = useRef(0);
  const scrollLockRef = useRef(0);
  const onBookDemoPage = location.pathname.startsWith("/book-demo");

  const openDemoIntake = () => {
    window.dispatchEvent(new CustomEvent("zta:open-demo-intake"));
  };

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    ignoreToggleUntilRef.current = Date.now() + 450;
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    scrollLockRef.current = window.scrollY;
    const { body, documentElement } = document;
    const previous = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollLockRef.current}px`;
    body.style.width = "100%";
    body.classList.add("zta-nav-open");
    documentElement.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      body.style.overflow = previous.overflow;
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.classList.remove("zta-nav-open");
      documentElement.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
      window.scrollTo(0, scrollLockRef.current);
    };
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  const toggleMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (Date.now() < ignoreToggleUntilRef.current) return;
    setMenuOpen((open) => !open);
  }, []);

  const handleMenuTouchEnd = (event) => {
    skipClickRef.current = true;
    toggleMenu(event);
  };

  const handleMenuClick = (event) => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      event.preventDefault();
      return;
    }
    toggleMenu(event);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-[1110] transition-colors duration-200 ${
          scrolled
            ? "bg-[#09090b]/95 md:bg-black/60 md:backdrop-blur-2xl border-b border-white/[0.06]"
            : "bg-[#09090b]/80 md:bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" className="flex-shrink-0 relative z-[1111]" aria-label="Zion Trading Algos home">
            <img
              src={logoImage}
              alt="Zion Trading Algos"
              className="h-8 md:h-9 w-auto object-contain"
              width={376}
              height={396}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-white bg-white/[0.08]"
                    : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="hidden md:block">
            {onBookDemoPage ? (
              <button
                type="button"
                onClick={() => {
                  gtagTrackBookDemo();
                  openDemoIntake();
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand hover:bg-brand-light text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-brand/20 hover:shadow-brand/30 hover:scale-[1.02]"
              >
                Book a Demo
              </button>
            ) : (
              <Link
                to="/book-demo"
                onClick={gtagTrackBookDemo}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand hover:bg-brand-light text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-brand/20 hover:shadow-brand/30 hover:scale-[1.02]"
              >
                Book a Demo
              </Link>
            )}
          </div>

          <button
            type="button"
            onClick={handleMenuClick}
            onTouchEnd={handleMenuTouchEnd}
            className="zta-menu-toggle md:hidden"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
          >
            {menuOpen ? (
              <span className="zta-menu-close" aria-hidden="true">
                ×
              </span>
            ) : (
              <>
                <span />
                <span />
                <span />
              </>
            )}
          </button>
        </div>
      </nav>

      <div
        id="mobile-navigation"
        className={`zta-mobile-nav ${menuOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!menuOpen}
      >
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            onClick={closeMenu}
            className={`text-xl font-semibold py-3 px-6 rounded-xl text-center ${
              location.pathname === link.path
                ? "text-white bg-white/[0.06]"
                : "text-zinc-400"
            }`}
          >
            {link.label}
          </Link>
        ))}
        <Link
          to="/book-demo"
          onClick={() => {
            closeMenu();
            gtagTrackBookDemo();
            if (onBookDemoPage) openDemoIntake();
          }}
          className="mt-6 px-8 py-3.5 bg-brand text-white text-lg font-semibold rounded-xl"
        >
          Book a Demo
        </Link>
      </div>
    </>
  );
});

export default Navbar;
